// Bulk-paste modal — matches the legacy editor's showBulkModal at
// pcp_builder.html#3910. Renders a centered white card on a dark
// overlay with: title + icon, subtitle, textarea, live item counter,
// Cancel / "+ Add All" buttons. Returns a Promise that resolves to
// the trimmed paste text when the user clicks Add All, or null when
// they cancel / dismiss.
//
// Pass `multi: true` and a `fields` array to render the column-hint
// row beneath the textarea (e.g. for circleOfSupport, communication)
// so the user knows which "|"-separated columns map to what.

export function showBulkModal({ title, subtitle, placeholder, multi = false, fields = [] } = {}) {
  return new Promise((resolve) => {
    document.getElementById("bulk-modal-overlay")?.remove()
    const overlay = document.createElement("div")
    overlay.id = "bulk-modal-overlay"
    overlay.className = "fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4"
    const ph = (placeholder || "").replace(/\n/g, "&#10;")
    overlay.innerHTML = `
      <div class="bg-white rounded-xl shadow-2xl w-[600px] max-w-[90vw] flex flex-col">
        <div class="p-5 border-b border-gray-200">
          <h3 class="text-lg font-bold text-gray-800 flex items-center gap-2">
            <i data-lucide="list-plus" class="w-5 h-5 text-blue-500"></i>
            ${escape(title || "Bulk Add Items")}
          </h3>
          <p class="text-sm text-gray-500 mt-1">${escape(subtitle || "One item per line")}</p>
          ${multi && fields.length ? `
            <p class="text-xs text-gray-400 mt-2 italic">
              Separate columns with <code class="px-1 py-0.5 bg-gray-100 rounded text-gray-700 not-italic">|</code> in this order:
              <span class="font-semibold text-gray-600 not-italic">${fields.map(escape).join(" | ")}</span>
            </p>` : ""}
        </div>
        <div class="p-5">
          <textarea data-bulk-input
                    class="w-full h-48 p-4 border border-gray-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 leading-relaxed"
                    placeholder="${ph}"></textarea>
          <div class="flex items-center justify-between mt-2">
            <span data-bulk-count class="text-xs text-gray-400">0 items</span>
            <span class="text-[11px] text-gray-400">Tip: press <kbd class="px-1 py-0.5 bg-gray-100 rounded">Esc</kbd> to cancel</span>
          </div>
        </div>
        <div class="px-5 pb-5 flex justify-end gap-3">
          <button type="button" data-bulk-cancel
                  class="px-5 py-2.5 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium text-sm transition-colors">
            Cancel
          </button>
          <button type="button" data-bulk-submit
                  class="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm transition-colors flex items-center gap-2">
            <i data-lucide="plus" class="w-4 h-4"></i> Add All
          </button>
        </div>
      </div>`
    document.body.appendChild(overlay)
    const close = (val) => {
      document.removeEventListener("keydown", onKey)
      overlay.remove()
      resolve(val)
    }
    const onKey = (e) => {
      if (e.key === "Escape") close(null)
      if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) submit()
    }
    document.addEventListener("keydown", onKey)
    overlay.addEventListener("click", (e) => { if (e.target === overlay) close(null) })
    const ta      = overlay.querySelector("[data-bulk-input]")
    const counter = overlay.querySelector("[data-bulk-count]")
    const submit  = () => {
      const v = (ta.value || "").trim()
      if (!v) { ta.focus(); return }
      close(v)
    }
    ta.addEventListener("input", () => {
      const n = ta.value.split("\n").filter(s => s.trim()).length
      counter.textContent = `${n} item${n === 1 ? "" : "s"}`
    })
    overlay.querySelector("[data-bulk-cancel]").addEventListener("click", () => close(null))
    overlay.querySelector("[data-bulk-submit]").addEventListener("click", submit)
    setTimeout(() => {
      ta.focus()
      if (window.lucide) window.lucide.createIcons()
    }, 30)
  })
}

function escape(s) {
  return String(s == null ? "" : s)
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;")
}
